import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../services/axios';
import { getToken, setToken, removeToken, getRefreshToken, setRefreshToken, removeRefreshToken } from '../utils/storage';
import { stripInternalFields } from '../utils/privacy';
import { getPrimaryNavRole } from '../config/navigation';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

const collectPermissions = (roles = []) => {
    const seen = new Set();
    const permissions = [];
    roles.forEach((role) => {
        (role.permissions || role.Permissions || []).forEach((permission) => {
            const key = `${permission.resource}:${permission.action}`;
            if (!seen.has(key)) {
                seen.add(key);
                permissions.push({ resource: permission.resource, action: permission.action });
            }
        });
    });
    return permissions;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);

    const applySession = (userData, roleData = []) => {
        setUser(userData ? stripInternalFields(userData) : null);
        setRoles(roleData);
        setPermissions(collectPermissions(roleData));
    };

    useEffect(() => {
        const loadUser = async () => {
            const token = getToken();
            if (token) {
                try {
                    const response = await axios.get('/auth/me');
                    applySession(response.data.data.user, response.data.data.roles || []);
                } catch (error) {
                    console.error('Failed to load user:', error);
                    await refreshToken();
                }
            }
            setLoading(false);
        };

        loadUser();
    }, []);

    const refreshToken = async () => {
        try {
            const token = getRefreshToken();
            if (!token) return false;

            const response = await axios.post('/auth/refresh-token', { refreshToken: token });
            setToken(response.data.data.accessToken);
            setRefreshToken(response.data.data.refreshToken);
            return true;
        } catch (error) {
            removeToken();
            removeRefreshToken();
            applySession(null, []);
            return false;
        }
    };

    const login = async (email, password) => {
        try {
            const response = await axios.post('/auth/login', { email, password });
            const { user, accessToken, refreshToken } = response.data.data;

            setToken(accessToken);
            setRefreshToken(refreshToken);

            let nextRoles = user?.Roles || user?.roles || [];
            let currentUser = user;

            try {
                const rolesResponse = await axios.get('/auth/me');
                if (rolesResponse?.data?.data) {
                    nextRoles = rolesResponse.data.data.roles || nextRoles;
                    currentUser = rolesResponse.data.data.user || currentUser;
                }
            } catch (meError) {
                console.warn('Optional /auth/me call after login failed, using login payload fallback:', meError);
            }

            applySession(currentUser, nextRoles);

            toast.success('Login successful! Welcome back.');
            return {
                success: true,
                user: currentUser,
                navRole: getPrimaryNavRole(nextRoles)
            };
        } catch (error) {
            toast.error(error.response?.data?.message || 'Login failed');
            return { success: false, error: error.response?.data?.message };
        }
    };

    const logout = async () => {
        try {
            const token = getRefreshToken();
            if (token) {
                await axios.post('/auth/logout', { refreshToken: token });
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            removeToken();
            removeRefreshToken();
            applySession(null, []);
            toast.success('Logged out successfully');
        }
    };

    const hasPermission = (resource, action) => {
        if (roles.some((role) => role.name === 'super_admin')) {
            return true;
        }
        if (!resource || !action) return false;
        return permissions.some(
            (permission) => permission.resource === resource && permission.action === action
        );
    };

    const navRole = getPrimaryNavRole(roles);

    const value = {
        user,
        setUser: (next) => setUser(stripInternalFields(next)),
        roles,
        permissions,
        loading,
        login,
        logout,
        refreshToken,
        hasPermission,
        navRole,
        isAuthenticated: !!user,
        isAdmin: roles.some((role) => ['super_admin', 'program_manager'].includes(role.name)),
        isMentor: roles.some((role) => role.name === 'mentor'),
        isIntern: navRole === 'intern',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
